import { useMemo, useState } from "react";
import type {
  FatigueLevel,
  FootStatus,
  JointPainState,
  MuscleSoreness,
  SleepQuality,
} from "@jilian/shared";
import type { ReadinessResult } from "@jilian/core";
import { evaluateReadiness } from "@jilian/core";
import { planTypeTitle, planTypeDescription } from "../lib/copy";
import { Button, Card, RiskTag, Segmented } from "./ui";

export function ReadinessForm({ onComplete }: { onComplete: (r: ReadinessResult) => void }) {
  const [sleep, setSleep] = useState<SleepQuality>("good");
  const [fatigue, setFatigue] = useState<FatigueLevel>("low");
  const [soreness, setSoreness] = useState<MuscleSoreness>("none");
  const [foot, setFoot] = useState<FootStatus>("stable");
  const [joint, setJoint] = useState<JointPainState>("none");

  const result = useMemo(
    () =>
      evaluateReadiness({
        sleepQuality: sleep,
        fatigueLevel: fatigue,
        muscleSoreness: soreness,
        footStatus: foot,
        jointPain: joint,
      }),
    [sleep, fatigue, soreness, foot, joint],
  );

  const isNormalAll =
    sleep === "good" && fatigue === "low" && soreness === "none" && foot === "stable" && joint === "none";

  const handleResetToNormal = () => {
    setSleep("good");
    setFatigue("low");
    setSoreness("none");
    setFoot("stable");
    setJoint("none");
  };

  return (
    <div className="space-y-4 pb-2">
      {/* 快捷全部正常确认 */}
      <div className="flex items-center justify-between px-1">
        <span className="text-[12px] font-medium text-tone-soft font-caption">
          智能默认预填（偏离时再微调）：
        </span>
        {!isNormalAll && (
          <button
            type="button"
            onClick={handleResetToNormal}
            className="text-[12px] font-bold text-tone-accent hover:underline active:scale-95"
          >
            重置为全部正常
          </button>
        )}
      </div>

      <Card className="p-4 space-y-3.5 border border-tone-border">
        <div>
          <div className="text-[12px] font-semibold text-tone-soft mb-1 font-caption">昨晚睡眠</div>
          <Segmented<SleepQuality>
            value={sleep}
            onChange={setSleep}
            options={[
              { value: "poor", label: "差" },
              { value: "moderate", label: "一般" },
              { value: "good", label: "良好" },
            ]}
          />
        </div>
        <div>
          <div className="text-[12px] font-semibold text-tone-soft mb-1 font-caption">整体疲劳感</div>
          <Segmented<FatigueLevel>
            value={fatigue}
            onChange={setFatigue}
            options={[
              { value: "low", label: "精力充沛 (低)" },
              { value: "medium", label: "轻度疲劳 (中)" },
              { value: "high", label: "明显疲乏 (高)" },
            ]}
          />
        </div>
        <div>
          <div className="text-[12px] font-semibold text-tone-soft mb-1 font-caption">目标肌肉酸痛</div>
          <Segmented<MuscleSoreness>
            value={soreness}
            onChange={setSoreness}
            options={[
              { value: "none", label: "无酸痛" },
              { value: "mild", label: "轻微酸痛" },
              { value: "significant", label: "明显酸痛" },
            ]}
          />
        </div>
        <div>
          <div className="text-[12px] font-semibold text-tone-soft mb-1 font-caption">右小趾与足部状态</div>
          <Segmented<FootStatus>
            value={foot}
            onChange={setFoot}
            tone={foot === "significantly_worse" ? "danger" : foot === "slightly_worse" ? "warn" : "good"}
            options={[
              { value: "stable", label: "稳定无异样" },
              { value: "slightly_worse", label: "略有不适" },
              { value: "significantly_worse", label: "明显肿痛" },
            ]}
          />
        </div>
        <div>
          <div className="text-[12px] font-semibold text-tone-soft mb-1 font-caption">主要关节是否有刺痛</div>
          <Segmented<JointPainState>
            value={joint}
            onChange={setJoint}
            tone={joint === "present" ? "danger" : "accent"}
            options={[
              { value: "none", label: "完全无痛" },
              { value: "present", label: "有刺痛感" },
            ]}
          />
        </div>
      </Card>

      {/* 规则引擎判定结果预览 */}
      <Card className="p-4 border border-tone-border bg-black/[0.01] dark:bg-white/[0.02]">
        <div className="flex items-center gap-2 mb-1.5">
          <RiskTag
            tone={
              result.planType === "normal"
                ? "good"
                : result.planType === "injury_conservative"
                  ? "danger"
                  : "warn"
            }
          >
            {planTypeTitle(result.planType)}
          </RiskTag>
          <span className="text-tone-soft text-[12px] font-caption">
            体量 ×{result.volumeMultiplier} · 负荷 ×{result.loadMultiplier} · 进阶：
            {result.allowProgression ? "正常开启" : "暂停加重"}
          </span>
        </div>
        <p className="text-[13px] text-tone-ink font-body">{planTypeDescription(result.planType)}</p>
      </Card>

      <Button className="w-full min-h-[50px] font-bold text-[16px]" onClick={() => onComplete(result)}>
        应用评估并开始训练
      </Button>

      <p className="text-[11px] text-tone-soft text-center font-caption">
        下肢承重阶段与复合动作解锁，应依据专业医疗许可；本工具不诊断、不判决承重。
      </p>
    </div>
  );
}
